<template>
  <div class="quiz-management">
    <!-- Quiz Control Panel -->
    <div v-if="!activeQuiz" class="quiz-creation-panel">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <h5 class="mb-0">Quiz Management</h5>
        <b-button
          variant="primary"
          size="sm"
          @click="showCreateQuizModal = true"
          :disabled="isLoading"
        >
          <feather-icon icon="PlusIcon" size="16" class="mr-1" />
          Create Quiz
        </b-button>
      </div>

      <!-- Available Test Packs -->
      <div v-if="testPacks.length > 0" class="test-packs-list">
        <label class="form-label text-sm text-muted mb-2">Quick Start from Test Pack:</label>
        <div class="test-packs-grid">
          <div
            v-for="testPack in testPacks"
            :key="testPack.id"
            class="test-pack-card"
            @click="selectTestPack(testPack)"
          >
            <div class="test-pack-header">
              <h6 class="mb-1">{{ testPack.title }}</h6>
              <span class="badge badge-light">{{ testPack.questions_count }} questions</span>
            </div>
            <p class="test-pack-description text-muted text-sm mb-0">
              {{ testPack.description || 'No description' }}
            </p>
          </div>
        </div>
      </div>

      <!-- No Test Packs Message -->
      <div v-else class="text-center py-4">
        <feather-icon icon="FileTextIcon" size="48" class="text-muted mb-2" />
        <p class="text-muted">No test packs available</p>
      </div>
    </div>

    <!-- Active Quiz Controls -->
    <div v-else class="active-quiz-panel">
      <div class="quiz-header">
        <div class="quiz-info mb-3">
          <h5 class="mb-2">{{ activeQuiz.title }}</h5>
          <span class="badge" :class="getQuizStatusClass()">
            {{ getQuizStatusText() }}
          </span>
        </div>
        <div class="quiz-actions d-flex gap-2 flex-wrap">
          <b-button
            v-if="activeQuiz.status === 'created'"
            variant="success"
            size="md"
            @click="startQuiz"
            :disabled="isLoading"
          >
            <feather-icon icon="PlayIcon" size="16" />
            <span class="ml-1">Start Quiz</span>
          </b-button>
          <b-button
            v-if="activeQuiz.status === 'active'"
            variant="warning"
            size="md"
            @click="endQuiz"
            :disabled="isLoading"
          >
            <feather-icon icon="StopCircleIcon" size="16" />
            <span class="ml-1">End Quiz</span>
          </b-button>
          <b-button
            variant="primary"
            size="md"
            @click="showQuizResults = true"
            :disabled="activeQuiz.status === 'created'"
          >
            <feather-icon icon="BarChart2Icon" size="16" />
            <span class="ml-1">View Results</span>
          </b-button>
        </div>
      </div>

      <!-- Quiz Statistics -->
      <div v-if="activeQuiz.status !== 'created'" class="quiz-stats mt-3">
        <div class="row g-3">
          <div class="col-6">
            <div class="stat-card">
              <div class="stat-icon">
                <feather-icon icon="UsersIcon" size="20" />
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ quizStats.participant_count || 0 }}</div>
                <div class="stat-label">Participants</div>
              </div>
            </div>
          </div>
          <div class="col-6">
            <div class="stat-card">
              <div class="stat-icon">
                <feather-icon icon="CheckCircleIcon" size="20" />
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ quizStats.completed_submissions || 0 }}</div>
                <div class="stat-label">Completed</div>
              </div>
            </div>
          </div>
          <div class="col-6">
            <div class="stat-card">
              <div class="stat-icon">
                <feather-icon icon="TrendingUpIcon" size="20" />
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ Math.round(quizStats.average_score || 0) }}%</div>
                <div class="stat-label">Avg Score</div>
              </div>
            </div>
          </div>
          <div class="col-6">
            <div class="stat-card">
              <div class="stat-icon">
                <feather-icon icon="ClockIcon" size="20" />
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ formatDuration(quizStats.duration) }}</div>
                <div class="stat-label">Duration</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Real-time Submissions -->
      <div v-if="activeQuiz.status === 'active'" class="recent-submissions mt-3">
        <h6>Recent Submissions</h6>
        <div v-if="recentSubmissions.length === 0" class="text-center py-2 text-muted">
          No submissions yet
        </div>
        <div v-else class="submissions-list">
          <div
            v-for="submission in recentSubmissions"
            :key="submission.id"
            class="submission-item"
          >
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <div class="student-avatar">
                  {{ submission.user_name.charAt(0).toUpperCase() }}
                </div>
                <div class="ml-2">
                  <div class="student-name">{{ submission.user_name }}</div>
                  <div class="submission-time text-muted text-sm">
                    {{ formatTime(submission.submitted_at) }}
                  </div>
                </div>
              </div>
              <div class="submission-score">
                <span class="badge badge-success">{{ Math.round(submission.score) }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Quiz Modal -->
    <b-modal
      v-model="showCreateQuizModal"
      title="Create New Quiz"
      size="lg"
      @hidden="resetCreateForm"
    >
      <form @submit.prevent="createQuiz">
        <div class="form-group">
          <label for="quiz-title">Quiz Title</label>
          <b-input
            id="quiz-title"
            v-model="createForm.title"
            placeholder="Enter quiz title"
            required
          />
        </div>

        <div class="form-group">
          <label for="test-pack">Test Pack</label>
          <b-select
            id="test-pack"
            v-model="createForm.testPackId"
            :options="testPackOptions"
            required
          />
        </div>

        <div class="form-group">
          <label>Quiz Settings</label>
          <div class="settings-grid">
            <div class="form-check">
              <input
                id="show-results"
                v-model="createForm.showResultsImmediately"
                type="checkbox"
                class="form-check-input"
              />
              <label for="show-results" class="form-check-label">
                Show results immediately after submission
              </label>
            </div>
            <div class="form-check">
              <input
                id="randomize-questions"
                v-model="createForm.randomizeQuestions"
                type="checkbox"
                class="form-check-input"
              />
              <label for="randomize-questions" class="form-check-label">
                Randomize question order
              </label>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label for="time-limit">Time Limit (minutes)</label>
              <b-input
                id="time-limit"
                v-model="createForm.timeLimitMinutes"
                type="number"
                min="1"
                max="120"
                placeholder="No time limit"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label for="attempts-allowed">Attempts Allowed</label>
              <b-select
                id="attempts-allowed"
                v-model="createForm.attemptsAllowed"
                :options="[
                  { value: 1, text: '1 attempt' },
                  { value: 2, text: '2 attempts' },
                  { value: 3, text: '3 attempts' },
                  { value: 0, text: 'Unlimited' }
                ]"
              />
            </div>
          </div>
        </div>
      </form>

      <template #modal-footer="{ cancel, ok }">
        <b-button variant="outline-secondary" @click="cancel">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          @click="createQuiz"
          :disabled="isLoading || !createForm.title || !createForm.testPackId"
        >
          <b-spinner v-if="isLoading" small class="mr-1" />
          Create Quiz
        </b-button>
      </template>
    </b-modal>

    <!-- Quiz Results Modal -->
    <b-modal
      v-model="showQuizResults"
      title="Quiz Results"
      size="xl"
      @shown="loadQuizResults"
    >
      <quiz-results
        v-if="showQuizResults && activeQuiz"
        :quiz-id="activeQuiz.id"
        :webinar-id="webinarId"
      />
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import QuizResults from './QuizResults.vue'

export default {
  name: 'QuizManagement',

  components: {
    QuizResults
  },

  props: {
    webinarId: {
      type: [String, Number],
      required: true
    }
  },

  data() {
    return {
      isLoading: false,
      showCreateQuizModal: false,
      showQuizResults: false,
      createForm: {
        title: '',
        testPackId: null,
        showResultsImmediately: true,
        randomizeQuestions: false,
        timeLimitMinutes: null,
        attemptsAllowed: 1
      },
      recentSubmissions: []
    }
  },

  computed: {
    ...mapState('lessonRoom', ['testPacks', 'activeQuiz', 'quizStats']),
    ...mapGetters('user', ['role']),

    testPackOptions() {
      return [
        { value: null, text: 'Select a test pack...' },
        ...this.testPacks.map(pack => ({
          value: pack.id,
          text: `${pack.title} (${pack.questions_count} questions)`
        }))
      ]
    }
  },

  async created() {
    await this.loadTestPacks()
    await this.loadActiveQuiz()
    this.setupQuizEventListeners()
  },

  beforeDestroy() {
    this.cleanupEventListeners()
  },

  methods: {
    ...mapActions('lessonRoom', [
      'loadTestPacks',
      'loadActiveQuiz',
      'createQuiz',
      'startQuiz',
      'endQuiz',
      'loadQuizResults'
    ]),

    async selectTestPack(testPack) {
      this.createForm.title = `${testPack.title} Quiz`
      this.createForm.testPackId = testPack.id
      this.showCreateQuizModal = true
    },

    async createQuiz() {
      if (this.isLoading) return

      this.isLoading = true
      try {
        await this.$store.dispatch('lessonRoom/createQuiz', {
          webinarId: this.webinarId,
          ...this.createForm
        })

        this.showCreateQuizModal = false
        this.$bvToast.toast('Quiz created successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        })
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to create quiz', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.isLoading = false
      }
    },

    async startQuiz() {
      if (this.isLoading) return

      this.isLoading = true
      try {
        await this.$store.dispatch('lessonRoom/startQuiz', {
          webinarId: this.webinarId,
          quizId: this.activeQuiz.id
        })

        this.$bvToast.toast('Students can now take the quiz', {
          title: 'Quiz Started',
          variant: 'success',
          solid: true
        })
      } catch (error) {
        this.$bvToast.toast('Failed to start quiz', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.isLoading = false
      }
    },

    async endQuiz() {
      if (this.isLoading) return

      this.isLoading = true
      try {
        await this.$store.dispatch('lessonRoom/endQuiz', {
          webinarId: this.webinarId,
          quizId: this.activeQuiz.id
        })

        this.$bvToast.toast('Quiz has been ended and results are available', {
          title: 'Quiz Ended',
          variant: 'info',
          solid: true
        })
      } catch (error) {
        this.$bvToast.toast('Failed to end quiz', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.isLoading = false
      }
    },

    setupQuizEventListeners() {
      if (window.Echo) {
        this.echoChannel = window.Echo.private(`lesson-room.${this.webinarId}`)
          .listen('.quiz.submission', (event) => {
            this.handleQuizSubmission(event)
          })
          .listen('.quiz.started', (event) => {
            this.handleQuizStarted(event)
          })
          .listen('.quiz.ended', (event) => {
            this.handleQuizEnded(event)
          })
      }
    },

    cleanupEventListeners() {
      if (this.echoChannel) {
        this.echoChannel.stopListening('.quiz.submission')
        this.echoChannel.stopListening('.quiz.started')
        this.echoChannel.stopListening('.quiz.ended')
      }
    },

    handleQuizSubmission(event) {
      this.recentSubmissions.unshift(event.submission)
      if (this.recentSubmissions.length > 5) {
        this.recentSubmissions.pop()
      }

      // Update quiz stats
      this.$store.commit('lessonRoom/UPDATE_QUIZ_STATS', {
        completed_submissions: this.quizStats.completed_submissions + 1
      })
    },

    handleQuizStarted(event) {
      this.$store.commit('lessonRoom/SET_ACTIVE_QUIZ', event.quiz)
    },

    handleQuizEnded(event) {
      this.$store.commit('lessonRoom/UPDATE_QUIZ_STATS', event.statistics)
      this.$store.commit('lessonRoom/UPDATE_ACTIVE_QUIZ', { status: 'ended' })
    },

    getQuizStatusClass() {
      const status = this.activeQuiz?.status
      return {
        'badge-secondary': status === 'created',
        'badge-success': status === 'active',
        'badge-info': status === 'ended'
      }
    },

    getQuizStatusText() {
      const status = this.activeQuiz?.status
      return {
        'created': 'Ready to Start',
        'active': 'In Progress',
        'ended': 'Completed'
      }[status] || 'Unknown'
    },

    resetCreateForm() {
      this.createForm = {
        title: '',
        testPackId: null,
        showResultsImmediately: true,
        randomizeQuestions: false,
        timeLimitMinutes: null,
        attemptsAllowed: 1
      }
    },

    formatDuration(minutes) {
      if (!minutes) return '--'
      if (minutes < 60) return `${minutes}m`
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60
      return `${hours}h ${mins}m`
    },

    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString()
    },

    loadQuizResults() {
      this.$store.dispatch('lessonRoom/loadQuizResults', {
        webinarId: this.webinarId,
        quizId: this.activeQuiz.id
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.quiz-management {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  min-height: 400px;

  .gap-2 {
    gap: 0.5rem;
  }

  .test-packs-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 0.75rem;
    margin-top: 0.5rem;
  }

  .quiz-creation-panel {
    h5 {
      color: #2c3e50;
      font-weight: 600;
    }
  }

  .test-pack-card {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 1rem;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);

    &:hover {
      border-color: #7367f0;
      box-shadow: 0 4px 8px rgba(115, 103, 240, 0.15);
      transform: translateY(-2px);
    }

    .test-pack-header {
      display: flex;
      align-items: start;
      justify-content: space-between;
      margin-bottom: 0.5rem;

      h6 {
        color: #495057;
        font-weight: 600;
      }
    }

    .test-pack-description {
      font-size: 0.875rem;
      line-height: 1.3;
    }
  }

  .active-quiz-panel {
    padding: 0;
  }

  .quiz-header {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-radius: 8px;
    padding: 1.5rem;
    margin-bottom: 1.25rem;
    box-shadow: 0 2px 6px rgba(0,0,0,0.08);
    border: 1px solid #e9ecef;

    .quiz-info {
      h5 {
        color: #2c3e50;
        font-weight: 700;
        font-size: 1.25rem;
        line-height: 1.3;
      }

      .badge {
        font-size: 0.8rem;
        padding: 0.4rem 0.75rem;
        font-weight: 600;
        border-radius: 6px;
      }
    }

    .quiz-actions {
      display: flex;
      gap: 0.75rem;
      flex-wrap: wrap;

      .btn {
        white-space: nowrap;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0.65rem 1.5rem;
        font-weight: 600;
        font-size: 0.95rem;
        border-radius: 6px;
        transition: all 0.2s;

        svg {
          flex-shrink: 0;
        }

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.15);
        }
      }
    }
  }

  .quiz-stats {
    .row {
      margin: -0.5rem;

      > [class*="col-"] {
        padding: 0.5rem;
      }
    }

    .stat-card {
      background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
      border-radius: 8px;
      padding: 1.25rem 1rem;
      border: 1px solid #e9ecef;
      display: flex;
      align-items: center;
      gap: 1rem;
      transition: all 0.2s;
      box-shadow: 0 2px 4px rgba(0,0,0,0.05);
      height: 100%;

      &:hover {
        box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        transform: translateY(-2px);
      }

      .stat-icon {
        width: 48px;
        height: 48px;
        border-radius: 8px;
        background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        flex-shrink: 0;
      }

      .stat-content {
        flex: 1;
        text-align: left;

        .stat-number {
          font-size: 1.75rem;
          font-weight: 700;
          color: #2c3e50;
          line-height: 1;
          margin-bottom: 0.25rem;
        }

        .stat-label {
          color: #6c757d;
          font-size: 0.8rem;
          text-transform: uppercase;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
      }
    }
  }

  .recent-submissions {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-radius: 8px;
    padding: 1.25rem;
    box-shadow: 0 2px 6px rgba(0,0,0,0.08);
    border: 1px solid #e9ecef;

    h6 {
      color: #2c3e50;
      margin-bottom: 1rem;
      font-weight: 600;
      font-size: 1rem;
    }

    .submissions-list {
      max-height: 250px;
      overflow-y: auto;
    }

    .submission-item {
      padding: 0.5rem 0;
      border-bottom: 1px solid #f1f3f4;

      &:last-child {
        border-bottom: none;
      }

      .student-avatar {
        width: 32px;
        height: 32px;
        background: #007bff;
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 0.875rem;
      }

      .student-name {
        font-weight: 500;
        color: #495057;
      }

      .submission-time {
        font-size: 0.75rem;
      }
    }
  }

  .settings-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.5rem;

    .form-check {
      padding: 0.5rem;
      background: #f8f9fa;
      border-radius: 4px;
    }
  }
}
</style>